// DRHPWorkspace.jsx — DRHP Drafting Workspace (isolated, separate build).
//
// Multi-phase shell wired to the live /api/drhp-workspace backend:
//   LANDING          → command-centre overview (MainLandingDesk)
//   DATA_COLLECTION  → Master Data capture (MasterDataCollection)
//   LINKED_MAP       → Linked Fields Map → Submit & download integrated DRHP
//   DRAFTING         → the chapter editor (Editor + ChaptersRail + RightPanel)
// A bottom tab bar switches between Overview / Data Collection / Drafting.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "./lib/api.js";
import Editor from "./components/Editor.jsx";
import ChaptersRail from "./components/ChaptersRail.jsx";
import RightPanel from "./components/RightPanel.jsx";
import MainLandingDesk from "./components/MainLandingDesk.jsx";
import MasterDataCollection from "./components/MasterDataCollection.jsx";
import LinkedFieldsMap from "./components/LinkedFieldsMap.jsx";
import WorkspaceRefBar from "@/components/WorkspaceRefBar";
import { logJourney } from "@/lib/journey";
import "./workspace.css";
import "./masterData.css";
import "./mainLanding.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHASES = { LANDING: "LANDING", DATA_COLLECTION: "DATA_COLLECTION", LINKED_MAP: "LINKED_MAP", DRAFTING: "DRAFTING" };

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function DRHPWorkspace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Deep-link support: /drhp-workspace?phase=DATA_COLLECTION|LINKED_MAP|DRAFTING
  // (the Journey Planner "DRHP Drafting" steps link straight to the right phase).
  const initialPhase = PHASES[(searchParams.get("phase") || "").toUpperCase()] || PHASES.LANDING;
  const [phase, setPhase] = useState(initialPhase);

  // Drafting-editor state (loaded lazily when the DRAFTING phase is entered).
  const [chapters, setChapters] = useState([]);
  const [chapter, setChapter] = useState(null);
  const [me, setMe] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [trackOn, setTrackOn] = useState(true);
  const [editor, setEditor] = useState(null);
  const [docHtml, setDocHtml] = useState(null);
  const exportRef = useRef(null);
  const user = me ? { id: me.user_id, name: me.name || me.email, role: "owner" } : { id: "me", name: "You", role: "owner" };

  // Bind this DRHP workspace to the logged-in user's real company workspace
  // (shared slug) so it edits their DRHP, not the shared demo; the editor
  // identity is the logged-in user.
  useEffect(() => {
    fetch(`${API_URL}/api/workspace`, { credentials: "include" })
      .then((r) => r.json())
      .then((w) => { setWorkspace(w?.slug || "default"); setCompanyName(w?.company?.legal_name || ""); })
      .catch(() => setWorkspace("default"));
    fetch(`${API_URL}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => u && setMe(u))
      .catch(() => {});
    logJourney("DRHP Workspace", "Viewed", "Opened DRHP Workspace");
  }, []);

  // LINKED_MAP submit / download state.
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  async function loadDoc(ws, chId) {
    setDocHtml(null);
    try { const { html } = await api.getDoc(ws, chId); setDocHtml(html || "<p></p>"); }
    catch { setDocHtml("<p></p>"); }
  }

  // Lazy-init the drafting editor the first time the user enters it.
  useEffect(() => {
    if (phase !== PHASES.DRAFTING || chapters.length) return;
    (async () => {
      let cs = [];
      try { cs = await api.chapters(); } catch { cs = []; }
      setChapters(cs);
      const risk = cs.find((c) => /RISK FACTORS/i.test(c.title)) || cs[0];
      const chId = risk?.id || "front-matter";
      setChapter(chId);
      await loadDoc(workspace, chId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  function selectChapter(chId) { setChapter(chId); loadDoc(workspace, chId); }

  async function onImport(e) {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const { html } = await api.importDocx(file, { chapter, workspace, userId: user.id, userName: user.name });
    if (html) editor.commands.setContent(html);
  }

  // Go to the Linked Fields Map; optionally approve the master data first.
  function goLinkedMap(approve) {
    if (approve) toast.success("Master data approved — review the linked fields below.");
    setSubmitted(false); setSubmitResult(null);
    setPhase(PHASES.LINKED_MAP);
  }

  // Submit = final integrate (sync) so every linked placeholder is populated.
  async function onSubmit() {
    setSubmitting(true);
    try {
      const res = await api.syncMaster(workspace, { userId: user.id, userName: user.name });
      setSubmitResult(res); setSubmitted(true);
      toast.success(`Integrated — ${res?.replaced ?? 0} placeholder(s) populated. You can now download the DRHP.`);
    } catch { toast.error("Could not integrate the data."); }
    setSubmitting(false);
  }

  async function onDownloadFull() {
    setDownloading(true);
    try {
      const { blob } = await api.exportFull(workspace, { userId: user.id, userName: user.name });
      downloadBlob(blob, "DRHP_for_review.docx");
      toast.success("Downloading the integrated DRHP for review.");
    } catch { toast.error("Download failed."); }
    setDownloading(false);
  }

  return (
    <div className="drhp-ws-root" data-testid="drhp-workspace-page">
      <div className="ws-topbar">
        <div className="ws-left">
          <div className="ws-brand" onClick={() => navigate("/ipo-navigator")} data-testid="drhp-ws-brand">
            IPO<span>Navigator</span>
          </div>
          <button className="ws-navbtn" onClick={() => navigate("/home")} data-testid="drhp-ws-home" title="Home Page"><i className="ti ti-home" aria-hidden="true" /><span className="ws-navbtn-t">Home Page</span></button>
          <button className="ws-navbtn oracle" onClick={() => navigate("/oracle")} data-testid="drhp-ws-ask-oracle" title="Ask Oracle {IPO.GPT}"><i className="ti ti-robot" aria-hidden="true" /><span className="ws-navbtn-t">Ask Oracle {"{IPO.GPT}"}</span></button>
        </div>
      </div>

      <WorkspaceRefBar />

      <div className="shell">
        {/* Phase tab bar — top navigation */}
        <div className="ws-tabs" data-testid="drhp-ws-tabs">
          <div className={`ws-tab ${phase === PHASES.LANDING ? "on" : ""}`} onClick={() => setPhase(PHASES.LANDING)} data-testid="ws-tab-overview">
            <i className="ti ti-layout-dashboard" /> Overview
          </div>
          <div className={`ws-tab ${phase === PHASES.DATA_COLLECTION || phase === PHASES.LINKED_MAP ? "on" : ""}`} onClick={() => setPhase(PHASES.DATA_COLLECTION)} data-testid="ws-tab-data-collection">
            <i className="ti ti-database" /> Data Collection
          </div>
          <div className={`ws-tab ${phase === PHASES.DRAFTING ? "on" : ""}`} onClick={() => setPhase(PHASES.DRAFTING)} data-testid="ws-tab-drafting">
            <i className="ti ti-edit" /> Drafting Editor
          </div>
        </div>
        {!workspace && (
          <div style={{ padding: 28, fontSize: 13, color: "#8a8a94" }}>Loading your workspace…</div>
        )}
        {workspace && phase === PHASES.LANDING && (
          <MainLandingDesk workspace={workspace} onNavigate={setPhase}
            onExport={onDownloadFull} exporting={downloading} />
        )}

        {workspace && phase === PHASES.DATA_COLLECTION && (
          <MasterDataCollection workspace={workspace} user={user} onGoLinkedMap={goLinkedMap} />
        )}

        {workspace && phase === PHASES.LINKED_MAP && (
          <div data-testid="drhp-linked-map-phase">
            <div className="drhp-lf-root" style={{ marginBottom: 0 }}>
              <div className="lf-actions" style={{ borderRadius: "14px 14px 0 0", borderBottom: "none", marginBottom: -1 }}>
                <span className="lf-back" onClick={() => setPhase(PHASES.DATA_COLLECTION)} data-testid="lf-back-to-data">
                  <i className="ti ti-arrow-left" /> Back to Data Collection
                </span>
              </div>
            </div>
            <LinkedFieldsMap workspace={workspace} />
            <div className="drhp-lf-root">
              <div className="lf-actions" data-testid="lf-submit-footer">
                <span className="lf-status">
                  {submitted
                    ? <><i className="ti ti-circle-check" /> <span className="ok">Integrated{submitResult ? ` · ${submitResult.replaced} placeholders populated` : ""}</span></>
                    : <><i className="ti ti-info-circle" /> Review the linked fields, then submit to integrate every placeholder</>}
                </span>
                <span className="spacer" />
                <button className="lf-btn pri" onClick={onSubmit} disabled={submitting} data-testid="lf-submit-btn">
                  <i className="ti ti-check" /> {submitting ? "Integrating…" : "Submit & Integrate"}
                </button>
                <button className="lf-btn" onClick={onDownloadFull} disabled={!submitted || downloading} data-testid="lf-download-btn">
                  <i className="ti ti-download" /> {downloading ? "Preparing…" : "Download document for review"}
                </button>
              </div>
            </div>
          </div>
        )}

        {workspace && phase === PHASES.DRAFTING && (
          <>
            <h1 className="ws-title">DRHP drafting workspace</h1>
            <p className="ws-sub">
              Co-draft your Draft Red Herring Prospectus chapter by chapter. Tracked changes
              (who / when / what / why), comments, SEBI responses, audit trail, paste from
              Word / Excel / images, and export to Word.
              <span className="who-select" data-testid="drhp-ws-user-label" style={{ display: "inline-flex", alignItems: "center" }}>
                You: {user.name}
              </span>
              <span className="who-select" data-testid="drhp-ws-workspace-label" style={{ display: "inline-flex", alignItems: "center" }}>
                Company: {companyName || "Your company"}
              </span>
            </p>

            <div className="ws-grid">
              <ChaptersRail active={chapter} onSelect={selectChapter} chapters={chapters} />
              {chapter && docHtml != null ? (
                <Editor key={`${workspace}:${chapter}`} workspace={workspace} chapter={chapter} user={user}
                  trackOn={trackOn} setTrackOn={setTrackOn} initialHtml={docHtml}
                  onEditorReady={setEditor} registerExport={(fn) => { exportRef.current = fn(); }} />
              ) : (
                <div className="doc-area"><div style={{ padding: 20 }}>Loading chapter…</div></div>
              )}
              <RightPanel workspace={workspace} chapter={chapter} user={user} editor={editor} />
            </div>

            <div className="footer-actions">
              <label className="fa-btn" style={{ cursor: "pointer" }} data-testid="drhp-ws-import">
                Import .docx
                <input type="file" accept=".docx" onChange={onImport} hidden />
              </label>
              <button className="fa-btn pri" data-testid="drhp-ws-export"
                onClick={() => exportRef.current && exportRef.current()}>Export to Word</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
